import * as React from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  setAllGroupUser,
  setGroupUser,
} from "../../../redux/TaskGroupingSlice";
import { useUsers } from "../../../reactQuery/users";
import {
  createExpandedTeamPanels,
  getFilteredTeamUsers,
  getSearchExpandedTeamPanels,
  getUnassignedUsers,
  normalizeTeams,
} from "../utils/myTaskUtils";

export function useTeamFilterState(team) {
  const dispatch = useDispatch();
  const { users } = useUsers();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [openUnassigned, setUnassigned] = React.useState(false);
  const [expandedPanel, setExpandedPanel] = React.useState([]);
  const [wasSearching, setWasSearching] = React.useState(false);
  const groupingUser = useSelector((state) => state.groupingUser.allocationUsers);
  const selectedUsers = React.useMemo(
    () => groupingUser?.map((item) => item?.users).flat() || [],
    [groupingUser]
  );
  const open = Boolean(anchorEl);

  const allTeams = React.useMemo(
    () => normalizeTeams(team?.data?.data?.responseData),
    [team?.data?.data?.responseData]
  );

  const filteredItems = React.useMemo(
    () =>
      getFilteredTeamUsers({
        users: users?.data?.data?.responseData || [],
        teams: allTeams,
        searchQuery,
      }),
    [allTeams, searchQuery, users?.data?.data?.responseData]
  );

  const unAssignedTeam = React.useMemo(
    () =>
      getUnassignedUsers({
        filteredItems,
        selectedUsers,
      }),
    [filteredItems, selectedUsers]
  );

  const allUser = React.useMemo(
    () => filteredItems.filter((user) => user.active) || [],
    [filteredItems]
  );

  const visibleTeams = React.useMemo(
    () =>
      allTeams.filter(
        (teamItem) =>
          teamItem?.users?.length &&
          teamItem.users.some((teamUser) =>
            filteredItems.some((user) => user.user_id === teamUser.user_id)
          )
      ),
    [allTeams, filteredItems]
  );

  const handleExpandAccordianChange = React.useCallback(
    (teamId) => (_event, isExpanded) => {
      setExpandedPanel((prev) =>
        prev.map((element) =>
          element.team_id === teamId
            ? { ...element, isExpand: isExpanded }
            : element
        )
      );
    },
    []
  );

  React.useEffect(() => {
    if (allTeams.length) {
      setExpandedPanel(createExpandedTeamPanels(allTeams));
    }
  }, [allTeams]);

  React.useEffect(() => {
    if (searchQuery) {
      setWasSearching(true);
      setExpandedPanel(
        getSearchExpandedTeamPanels({
          teams: allTeams,
          filteredItems,
        })
      );
      if (unAssignedTeam.length > 0) {
        setUnassigned(true);
      }
      return;
    }

    if (wasSearching) {
      setWasSearching(false);
      setUnassigned(false);
      setExpandedPanel(createExpandedTeamPanels(allTeams));
    }
  }, [allTeams, filteredItems, searchQuery, unAssignedTeam.length, wasSearching]);

  const setTeam = React.useCallback(
    (teamUser, isChecked) => {
      dispatch(setAllGroupUser([]));

      if (isChecked) {
        selectedUsers.forEach((element) => {
          if (element.team_id !== teamUser.team_id) {
            dispatch(setGroupUser(element));
          }
        });
        return;
      }

      teamUser.users.forEach((element) => {
        dispatch(setGroupUser(element));
      });

      selectedUsers.forEach((element) => {
        if (element.team_id !== teamUser.team_id) {
          dispatch(setGroupUser(element));
        }
      });
    },
    [dispatch, selectedUsers]
  );

  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
    setSearchQuery("");
  }, []);

  const selectAll = React.useCallback(() => {
    dispatch(
      setAllGroupUser(
        selectedUsers.length === allUser.length
          ? []
          : [{ team: "Multiple", users: allUser }]
      )
    );
  }, [allUser, dispatch, selectedUsers.length]);

  return {
    anchorEl,
    open,
    searchQuery,
    openUnassigned,
    expandedPanel,
    selectedUsers,
    allTeams,
    filteredItems,
    unAssignedTeam,
    allUser,
    visibleTeams,
    setAnchorEl,
    setSearchQuery,
    setUnassigned,
    handleExpandAccordianChange,
    setTeam,
    handleClose,
    selectAll,
  };
}
